import { useCallback } from "react";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { getUserId } from "shared-frontend/functions/auth";

import {
	getGetAssignableSubscriptionsQueryKey,
	useGetAssignableSubscriptions,
} from "../api/generated/customer/customer";
import { useAddSubscription, useRemoveSubscription } from "../api/generated/site/site";

/**
 * The rows as they read before the query resolves and on error.
 *
 * An empty list rather than a null, so no consumer needs a loading branch: the site page renders its
 * chrome and an empty plugin list, which is what it did before the request resolved anyway.
 */
const NO_PLUGINS = Object.freeze( [] );

/**
 * Maps a plugin row onto the props the presentational components read.
 *
 * The row names its fields after what they hold — `productGroupName` is a product *group* name, and
 * `subscriptionIdForToggle` is one specific subscription of several — while the components predate that
 * and read `name`, `sourceId` and `subscriptionId`. Bridging here keeps the rename out of the
 * components and their snapshots.
 *
 * `subscriptionId` falls back to an empty string because `SiteSubscriptionRow` tests it against `""`
 * to decide whether a row is actionable; a null would make that comparison silently false.
 *
 * @param {Object} plugin The plugin row from the API.
 *
 * @returns {Object} The row as the components expect it.
 */
function toRowProps( plugin ) {
	return {
		...plugin,
		name: plugin.productGroupName,
		sourceId: plugin.productSourceId,
		subscriptionId: plugin.subscriptionIdForToggle ?? "",
		manageButtonSubscriptionId: plugin.manageButtonSubscriptionId ?? "",
	};
}

/**
 * The plugins assignable to one site, and the mutations that toggle them.
 *
 * Replaces the pipeline the site page used to run in the browser: the whole subscription collection,
 * the product catalogue, the product groups and the provisioner data, reduced client-side into
 * per-plugin slot counts. The server now ships the finished rows, so all four fetches go away.
 *
 * The two data-layer events are fired from the mutation's `onSuccess` at the call site, rather than from
 * an effect on the query data. That is deliberate and load-bearing: an effect keyed on query data
 * re-fires on every refetch — window refocus, reconnect, invalidation — so it would report cache activity
 * rather than something the user did, and this hook now refetches far more eagerly than it once did.
 * `onSuccess` runs exactly once per successful toggle, and never for a rejected one.
 *
 * @param {string} siteId The site whose plugins to read.
 *
 * @returns {Object} The rows, the query state, and a toggle callback.
 */
export default function useSiteAssignablePlugins( siteId ) {
	const customerId  = getUserId();
	const queryClient = useQueryClient();

	const { data, isLoading, isError } = useGetAssignableSubscriptions( customerId, siteId, {
		query: {
			// `getUserId()` returns an empty string when there is no access token, and the generated
			// `enabled` only guards null and undefined — so without this the hook would request
			// `Customers//sites/<id>/assignable-subscriptions`.
			enabled: Boolean( customerId && siteId ),
		},
	} );

	// Both mutations change which subscriptions are on this site, so both invalidate the same key. It is
	// cheap to refetch precisely because the response is bounded — which is what made moving the toggle
	// onto TanStack viable at all.
	const invalidate = useCallback(
		() => queryClient.invalidateQueries( {
			queryKey: getGetAssignableSubscriptionsQueryKey( customerId, siteId ),
		} ),
		[ queryClient, customerId, siteId ],
	);

	/**
	 * Writes the expected outcome of a toggle into the cache before the server confirms it.
	 *
	 * The toggle renders from this data, so without an optimistic write it would not move until the
	 * request came back — a few hundred milliseconds of a control that looks broken. Writing here rather
	 * than holding a copy inside the toggle is what keeps one source of truth: the row's slot badge reads
	 * the same object, so the two cannot disagree.
	 *
	 * Returns the previous rows, which `onError` restores. `cancelQueries` first, or an in-flight refetch
	 * could land after this write and overwrite it with pre-toggle data.
	 *
	 * @param {string}  subscriptionId The subscription being toggled.
	 * @param {boolean} enabled        Whether it is being enabled.
	 *
	 * @returns {Promise<Object>} The rollback context.
	 */
	const applyOptimistically = useCallback( async( subscriptionId, enabled ) => {
		const queryKey = getGetAssignableSubscriptionsQueryKey( customerId, siteId );
		await queryClient.cancelQueries( { queryKey } );

		const previous = queryClient.getQueryData( queryKey );

		queryClient.setQueryData( queryKey, current => ( current ?? [] ).map( plugin => {
			if ( plugin.subscriptionIdForToggle !== subscriptionId ) {
				return plugin;
			}

			// `used` moves with the assignment because the badge reports it; clamped so a double submit
			// cannot render a negative count.
			return {
				...plugin,
				isEnabled: enabled,
				used: enabled ? plugin.used + 1 : Math.max( 0, plugin.used - 1 ),
			};
		} ) );

		return { previous, queryKey };
	}, [ queryClient, customerId, siteId ] );

	/**
	 * Restores the pre-toggle rows after a failed mutation.
	 *
	 * @param {unknown} error   The mutation error.
	 * @param {unknown} variables The mutation variables.
	 * @param {Object}  context The rollback context.
	 *
	 * @returns {void}
	 */
	const rollback = useCallback( ( error, variables, context ) => {
		if ( context?.previous ) {
			queryClient.setQueryData( context.queryKey, context.previous );
		}
	}, [ queryClient ] );

	// Refetching on settle matters as much as the optimistic write, and on failure as much as on success.
	// A rejected toggle usually means the cache disagreed with the server — the subscription was
	// disconnected from its detail page, or activated from another tab — so the rolled-back rows are not
	// trustworthy either; only the server knows. Refetching either way is what makes the row correct
	// itself rather than waiting for the next focus.
	const addSubscription = useAddSubscription( {
		mutation: {
			onMutate: ( variables ) => applyOptimistically( variables.data.subscriptionId, true ),
			onError: rollback,
			onSettled: invalidate,
		},
	} );

	const removeSubscription = useRemoveSubscription( {
		mutation: {
			onMutate: ( { subscriptionId } ) => applyOptimistically( subscriptionId, false ),
			onError: rollback,
			onSettled: invalidate,
		},
	} );

	// Which subscriptions are in flight — plural, and read from the mutation cache rather than from the
	// two hooks above.
	//
	// A page-wide boolean was wrong because it disabled every row while one plugin settled. But the two
	// mutation objects cannot answer this either: both rows share one `useAddSubscription`, and each call
	// to `mutate` overwrites that single object's `variables`. So a second click reassigns the pending id
	// and the first row silently drops out of its pending state while its request is still running.
	// `useMutationState` reports every pending mutation, which is the only thing that scales past one.
	const pendingToggleVariables = useMutationState( {
		filters: { mutationKey: [ "addSubscription" ], status: "pending" },
		select: mutation => mutation.state.variables?.data?.subscriptionId,
	} );
	const pendingUntoggleVariables = useMutationState( {
		filters: { mutationKey: [ "removeSubscription" ], status: "pending" },
		select: mutation => mutation.state.variables?.subscriptionId,
	} );

	// A plain array, not a Set: it holds at most a handful of ids, and a new Set every render would
	// break the referential equality consumers memoise on.
	const togglingSubscriptionIds = [ ...pendingToggleVariables, ...pendingUntoggleVariables ]
		.filter( Boolean );

	return {
		plugins: data ? data.map( toRowProps ) : NO_PLUGINS,
		isLoading,
		isError,
		addSubscription,
		removeSubscription,
		togglingSubscriptionIds,
	};
}
